import { z } from 'zod';
import { JobConfigSchema } from './interview/config.js';
import { LANGUAGE_CODES, type LanguageCode } from './lang.js';

/**
 * Contracts shared by the server and the web app. The server validates every
 * request body against these; the web app imports the same types.
 */

export const OutreachModeSchema = z.enum(['WEB_LINK', 'PHONE_CALL', 'PHONE_THEN_WEB']);
export const JobStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'CLOSED']);

export const JobInputSchema = z.object({
  title: z.string().trim().min(2).max(120),
  department: z.string().trim().max(80).nullish(),
  location: z.string().trim().min(2).max(120),
  workMode: z.enum(['On-site', 'Hybrid', 'Remote']).default('On-site'),
  description: z.string().trim().min(20).max(6000),
  status: JobStatusSchema.default('DRAFT'),
  outreachMode: OutreachModeSchema.default('WEB_LINK'),
  interviewMinutes: z.number().int().min(5).max(45).default(15),
  maxCallAttempts: z.number().int().min(1).max(6).default(3),
  retryAfterMinutes: z.number().int().min(15).max(24 * 60).default(120),
  linkValidityHours: z.number().int().min(1).max(24 * 14).default(72),
  config: JobConfigSchema,
});
export type JobInput = z.infer<typeof JobInputSchema>;

export const StartInterviewsSchema = z.object({
  candidateIds: z.array(z.string().min(1)).min(1).max(500),
  /** Override the job's outreach mode for this batch. */
  mode: OutreachModeSchema.optional(),
});
export type StartInterviews = z.infer<typeof StartInterviewsSchema>;

export const DecisionKindSchema = z.enum(['SHORTLIST', 'REJECT', 'HOLD', 'ADVANCE', 'SELECT']);

export const DecisionInputSchema = z.object({
  /** SHORTLIST starts the skills assessment; ADVANCE invites to the recruiter interview; SELECT is the final yes. */
  kind: DecisionKindSchema,
  reason: z.string().trim().max(1000).optional(),
  /** REJECT / SELECT: email the candidate (the job's messages settings); unset follows the job's default. */
  notify: z.boolean().optional(),
});
export type DecisionInput = z.infer<typeof DecisionInputSchema>;

/** The same decision for many candidates of one job. */
export const BulkDecisionSchema = DecisionInputSchema.extend({
  kind: z.enum(['SHORTLIST', 'REJECT', 'HOLD', 'ADVANCE']),
  candidateIds: z.array(z.string().min(1).max(40)).min(1).max(200),
});
export type BulkDecision = z.infer<typeof BulkDecisionSchema>;

export const PasteImportSchema = z.object({
  text: z.string().min(1).max(2_000_000),
  filename: z.string().max(200).optional(),
});

export const LoginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1).max(200),
});

export const CandidateStatuses = [
  'NEW',
  'QUEUED',
  'INVITED',
  'CALLING',
  'IN_INTERVIEW',
  'INTERVIEWED',
  'QUALIFIED',
  'NOT_QUALIFIED',
  'NEEDS_REVIEW',
  'UNREACHABLE',
  'SHORTLISTED',
  'REJECTED',
  'ON_HOLD',
  'WITHDRAWN',
  // Stage 2, the AI skills assessment (after a shortlist)
  'ASSESSMENT_INVITED',
  'IN_ASSESSMENT',
  'ASSESSED',
  // Stage 3, the recruiter interview
  'MEETING_INVITED',
  'SELECTED',
] as const;
export type CandidateStatus = (typeof CandidateStatuses)[number];

// ---- Interview room (browser <-> server WebSocket) -------------------------------------
//
// Text frames carry JSON messages below. Binary frames:
//   client -> server: raw PCM16 little-endian mono at ROOM_SAMPLE_RATE
//   server -> client: [uint32 LE clip id][WAV bytes] - one clip per spoken sentence

export const ROOM_SAMPLE_RATE = 16_000;

export const IntegrityEvents = [
  'tab_hidden',
  'tab_visible',
  'window_blur',
  'paste',
  'mic_muted',
  'mic_unmuted',
] as const;

export const ClientMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('start'),
    language: z.enum(LANGUAGE_CODES as [LanguageCode, ...LanguageCode[]]),
    textMode: z.boolean().optional(),
  }),
  z.object({ type: z.literal('text'), text: z.string().trim().min(1).max(2000) }),
  z.object({ type: z.literal('played'), clip: z.number().int().nonnegative() }),
  z.object({ type: z.literal('integrity'), event: z.enum(IntegrityEvents) }),
  z.object({ type: z.literal('end') }),
  z.object({ type: z.literal('ping') }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

export type RoomState = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'ended';

export type ServerMessage =
  | { type: 'state'; state: RoomState }
  | { type: 'ai_text'; turn: number; text: string }
  | { type: 'candidate_text'; turn: number; text: string }
  | { type: 'clear' }
  | { type: 'ended'; reason: string }
  | { type: 'error'; message: string }
  | { type: 'pong' };

/** What the landing page shows before the candidate joins. */
export interface PublicInterviewInfo {
  candidateFirstName: string;
  jobTitle: string;
  company: string;
  interviewerName: string;
  minutes: number;
  languages: Array<{ code: LanguageCode; label: string; native: string }>;
  status: 'ready' | 'in_progress' | 'completed' | 'expired';
  /** livekit: join a LiveKit room (voice or video). builtin: the legacy WebSocket room. */
  runtime: 'livekit' | 'builtin';
  /** The interview's sections in order (introduction, background, logistics, competencies...). Optional. */
  sections?: Array<{ key: string; label: string }>;
  /** screening: the AI interview. assessment: the AI skills assessment. recruiter: the recruiter's video interview. */
  stage: 'screening' | 'assessment' | 'recruiter';
  /** Stage 2 only: what the rules page tells the candidate before they join. */
  assessment?: { tasks: number; minutes: number; handsOn: boolean; languages: string[] };
}

export const JoinRequestSchema = z.object({
  language: z.enum(LANGUAGE_CODES as [LanguageCode, ...LanguageCode[]]),
  mode: z.enum(['voice', 'video']),
});
export type JoinRequest = z.infer<typeof JoinRequestSchema>;

export interface JoinResponse {
  url: string;
  token: string;
  room: string;
}
